import React, { useEffect, useState } from "react";
import { getProductMovement } from "../../services/studioSalesApi";
import { unwrapGeoPayload } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";
import "../orders/orders.scss";

const kindLabel = (kind?: string, memoType?: string, memoStatus?: string) => {
  if (memoType === "APPROVAL") {
    if (kind === "MEMO_ACTIVE") return memoStatus === "DRAFT" ? "On a draft approval memo (not sent)" : "Out on approval";
    if (kind === "MEMO_RETURNED") return "Back from approval";
    if (kind === "MEMO_KEPT") return "Kept by the buyer (moved to a sale memo)";
    if (kind === "MEMO_DROPPED") return "Dropped from approval memo";
  }
  if (kind === "SALE") return "Sold";
  if (kind === "RETURN") return "Returned";
  if (kind === "SALE_REVERSED") return "Sale reversed";
  if (kind === "MEMO_ACTIVE") return "On billing memo";
  if (kind === "MEMO_BILLED") return "Billed on memo";
  if (kind === "MEMO_DROPPED") return "Dropped from memo";
  if (kind === "MEMO_VOIDED") return "Voided on memo";
  if (kind === "MEMO_RETURNED") return "Returned on memo";
  if (kind === "MEMO_REVERSED") return "Wrong bill reversal on memo";
  return kind || "Event";
};

const formatWhen = (value: any) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};

const ProductMovementLog = ({ productUniqueId }: { productUniqueId?: string }) => {
  const uid = String(productUniqueId || "").trim();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    if (!uid) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getProductMovement(uid)
      .then((res: any) => {
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setError(res?.message || "Could not load movement");
          setRows([]);
          return;
        }
        const payload = unwrapGeoPayload(res);
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setRows(list);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load movement");
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  if (!uid) {
    return null;
  }

  return (
    <div className="hkp-view-movement">
      <h3>Movement</h3>
      {loading ? <p>Loading…</p> : null}
      {error ? <p className="text-danger">{error}</p> : null}
      {!loading && !error && rows.length === 0 ? <p>No billing or ownership events yet.</p> : null}
      <ul className="order-note-list">
        {rows.map((row, index) => (
          <li key={`${row.eventId || row.memoId || "row"}-${index}`}>
            <div className="order-note-meta">{formatWhen(row.occurredAt)}</div>
            <strong>{kindLabel(row.kind, row.memoType, row.memoStatus)}</strong>
            {row.fromOwner || row.toOwner ? (
              <span>
                {" "}
                {row.fromOwner || "—"} → {row.toOwner || "—"}
              </span>
            ) : null}
            {row.memoCode ? <span> · {row.memoCode}</span> : null}
            {row.externalDocumentNumber || row.invoiceNumber ? <span> · {row.externalDocumentType || "Document"} {row.externalDocumentNumber || row.invoiceNumber}</span> : null}
            {row.sourceMemoCode && row.sourceMemoCode !== row.memoCode ? <span> · Source {row.sourceMemoCode}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ProductMovementLog;
